import { useEffect, useState, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { listRuns, getDashboardSummary } from "@/services/api"
import type { BackendRun } from "@/types"
import { Panel, Stat, StatGrid, Badge, SectionTitle } from "@/components/ui"
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts"

export default function RunsPage() {
  const navigate = useNavigate()
  const [runs, setRuns] = useState<BackendRun[]>([])
  const [summary, setSummary] = useState<any>(null)
  const [controllerFilter, setControllerFilter] = useState<string>("all")
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        const [r, s] = await Promise.all([listRuns(), getDashboardSummary()])
        if (!cancelled) {
          setRuns(r.data)
          setSummary(s.data)
        }
      } catch (e) {
        // degraded gracefully
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-muted">
        <p>Loading experiment history&hellip;</p>
      </div>
    )
  }

  const controllers = useMemo(
    () => ["all", ...Array.from(new Set(runs.map((r) => r.algorithm)))],
    [runs],
  )
  const filtered = runs.filter((r) => controllerFilter === "all" || r.algorithm === controllerFilter)

  const s = summary || {}
  const landedCount = runs.filter((r) => r.metrics?.landing_success === true).length

  // Aggregate landing rate by algorithm from metrics
  const byController = useMemo(() => {
    const map: Record<string, { total: number; landed: number }> = {}
    runs.forEach((r) => {
      const m = map[r.algorithm] ?? { total: 0, landed: 0 }
      m.total += 1
      if (r.metrics?.landing_success === true) m.landed += 1
      map[r.algorithm] = m
    })
    return Object.entries(map).map(([algorithm, m]) => ({
      algorithm,
      rate: m.total > 0 ? (m.landed / m.total) * 100 : 0,
    }))
  }, [runs])

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-ink tracking-tight">Experiment History</h1>
          <p className="text-muted text-sm">
            Recorded flight evaluations from the mission database
          </p>
        </div>
      </div>

      <StatGrid cols={4}>
        <Stat label="Total runs" value={runs.length.toLocaleString()} status="nominal" accent="blue" />
        <Stat label="Total experiments" value={s.total_experiments?.toLocaleString() ?? "0"} status="nominal" accent="cyan" />
        <Stat label="Controllers" value={controllers.length - 1} status="nominal" accent="amber" />
        <Stat label="Successful landings" value={landedCount.toLocaleString()} status={landedCount > 0 ? "safe" : "muted"} accent="cyan" />
      </StatGrid>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-3 xl:col-span-3 gap-4">
          <Panel title="Filter" padded={false}>
            <div className="p-3 space-y-2">
              <SectionTitle>Algorithm</SectionTitle>
              <div className="space-y-1">
                {controllers.map((c) => {
                  const active = controllerFilter === c
                  return (
                    <button
                      key={c}
                      onClick={() => setControllerFilter(c)}
                      className={`w-full text-left px-3 py-1.5 rounded-sm text-xs font-medium transition ${
                        active
                          ? "bg-cyan-500 text-black shadow-glow"
                          : "text-muted hover:text-ink hover:bg-surface/50"
                      }`}
                    >
                      {c === "all" ? "All" : c.toUpperCase()}
                    </button>
                  )
                })}
              </div>
            </div>
          </Panel>
        </div>

        <div className="lg:col-span-9 xl:col-span-9 gap-4">
          <Panel title="Landing rate by algorithm" padded={false}>
            <div className="p-3 h-[220px]">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart
                  data={byController}
                  margin={{ top: 8, right: 20, bottom: 8, left: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#1c2533" />
                  <XAxis
                    dataKey="algorithm"
                    tick={{ fontSize: 10, fill: "#5b6674" }}
                    axisLine={false}
                    tickMargin={8}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: "#5b6674" }}
                    axisLine={false}
                    tickMargin={8}
                    domain={[0, 100]}
                    label={{ value: "Landing rate (%)", position: "insideLeft", angle: -90, fontSize: 10, fill: "#5b6674" }}
                  />
                  <Tooltip
                    contentStyle={{ backgroundColor: "#0b1018", border: "1px solid #232e3e", color: "#e8edf6", fontSize: 11, borderRadius: 2, padding: 6 }}
                  />
                  <Bar dataKey="rate" fill="#22d3ee" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Panel>

          <Panel title="Runs" padded={false}>
            <div className="p-0 overflow-hidden">
              <div className="overflow-x-auto border-b border-border">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="px-3 py-2 text-[0.625rem] font-medium text-dim uppercase tracking-wider">ID</th>
                      <th className="px-3 py-2 text-[0.625rem] font-medium text-dim uppercase tracking-wider">Algorithm</th>
                      <th className="px-3 py-2 text-[0.625rem] font-medium text-dim uppercase tracking-wider">Obs</th>
                      <th className="px-3 py-2 text-[0.625rem] font-medium text-dim uppercase tracking-wider text-right">Status</th>
                      <th className="px-3 py-2 text-[0.625rem] font-medium text-dim uppercase tracking-wider text-right">Progress</th>
                      <th className="px-3 py-2 text-[0.625rem] font-medium text-dim uppercase tracking-wider text-right">Touchdown</th>
                      <th className="px-3 py-2 text-[0.625rem] font-medium text-dim uppercase tracking-wider text-right">Seed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-3 py-8 text-center text-muted text-sm">
                          No runs match the current filter.
                        </td>
                      </tr>
                    ) : (
                      filtered.map((r) => {
                        const m = r.metrics || {}
                        const ok = m.landing_success === true
                        return (
                          <tr
                            key={r.id}
                            className="border-b border-border/50 hover:bg-surface/50 transition group cursor-pointer"
                            onClick={() => navigate(`/runs/${r.id}`)}
                          >
                            <td className="px-3 py-2.5 text-xs font-mono text-muted truncate max-w-[90px]">
                              {r.id.slice(0, 8)}…
                            </td>
                            <td className="px-3 py-2.5 text-sm text-ink">{r.algorithm.toUpperCase()}</td>
                            <td className="px-3 py-2.5 text-xs text-dim">{r.obs_mode ?? "—"}</td>
                            <td className="px-3 py-2.5 text-xs font-mono text-ink text-right">
                              <Badge variant={r.status === "completed" ? "safe" : r.status === "running" ? "running" : "muted"}>
                                {r.status}
                              </Badge>
                            </td>
                            <td className="px-3 py-2.5 text-xs font-mono text-muted text-right">
                              {r.progress?.toFixed?.(0) ?? "—"}%
                            </td>
                            <td className="px-3 py-2.5 text-xs font-mono text-ink text-right">
                              {ok ? m.touchdown_velocity_mps?.toFixed?.(1) ?? "—" : "—"} m/s
                            </td>
                            <td className="px-3 py-2.5 text-xs font-mono text-dim text-right">{r.seed ?? "—"}</td>
                          </tr>
                        )
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  )
}
